import { logger, Message } from '@genesislcap/foundation-comms';
import { observable } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
import {
  INegotiation,
  IProposal,
  IVppa,
  IRfq,
  NegotiationDetails,
  ISCSelectedEntities,
} from '../types';
import { updateArray } from '../utils/dataserver';
import { getEnumReadableValue } from '../utils/formatting';
import { ICompanyService } from './company.service';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from './enum.service';
import { INegotiationService } from './negotiation.service';
import { IRfqService } from './rfq.service';
import { IVppaService } from './vppa.service';
import { CONTRACT_TYPE, NEGOTIATION_STATUS, PRODUCT_TYPE, ProfileVisibility } from '../utils';
import { IProjectService } from './project.service';
import { IUserService } from './user.service';
import { notifyError } from '../utils/notify';

export enum VALID_STRUCTURED_CHAT_STATUS {
  IN_PROGRESS = 'IN_PROGRESS',
  REQUIRES_CONFIRMATION = 'REQUIRES_CONFIRMATION',
  SUCCESSFUL = 'SUCCESSFUL',
  REJECTED = 'REJECTED',
  STAGED = 'STAGED',
  TRADED_AWAY = 'TRADED_AWAY',
}

export enum USED_STRUCTURED_CHAT_STATUS {
  CONFIRMED_AWAITING_BUYER = 'CONFIRMED_AWAITING_BUYER',
  CONFIRMED_AWAITING_SELLER = 'CONFIRMED_AWAITING_SELLER',
  IN_PROGRESS_ARTERIA = 'IN_PROGRESS_ARTERIA',
  REVIEW_EXECUTION_CHECKLIST = 'REVIEW_EXECUTION_CHECKLIST',
  READY_FOR_TRADE_EXECUTION = 'READY_FOR_TRADE_EXECUTION',
  PENDING_EXECUTION_TIMER = 'PENDING_EXECUTION_TIMER',
  PENDING_EXECUTION = 'PENDING_EXECUTION',
  EXECUTED_AWAITING_BUYER = 'EXECUTED_AWAITING_BUYER',
  EXECUTED_AWAITING_SELLER = 'EXECUTED_AWAITING_SELLER',
  EXPIRED = 'EXPIRED',
  BUYER_DELETED = 'BUYER_DELETED',
  SELLER_DELETED = 'SELLER_DELETED',
}

export interface IStructuredChatService {
  structuredChatVisibility: boolean;

  selectedVPPAIDs: string[];
  selectedRFQIDs: string[];
  selectedNegotiationID: string;
  warningMessage: string;
  allNegotiations: INegotiation[];
  allStagedNegotiations: INegotiation[];
  combinedNegotiations: INegotiation[];
  allProposals: IProposal[];
  negotiationInView: INegotiation;
  negotiationInViewDetails: NegotiationDetails;
  uniqueProjects: string[];
  disabledCounter: boolean;
  disabledAcceptReject: boolean;
  validStatusMap: Map<VALID_STRUCTURED_CHAT_STATUS, Array<string>>;

  openStructuredChat(selectedEntityIDs?: ISCSelectedEntities);
  closeStructuredChat();
  removeStagedNegotiation(negotiationID: string, newNegotiationID: string): void;
  isCompanyProposal(proposal?: IProposal): boolean;
  getLastProposalSenderName(): string;
  isCompanyAllowedToConfirmNegotiation(): boolean;
  isCompanyAllowedToReturnToNegotiation(): boolean;
}

class StructuredChatServiceImpl implements IStructuredChatService {
  @observable public structuredChatVisibility = false;

  @INegotiationService private negotiationService: INegotiationService;
  @IUserService private userService: IUserService;
  @IEnumService private enumService: IEnumService;
  @IVppaService private vppaService: IVppaService;
  @IRfqService private rfqService: IRfqService;
  @ICompanyService private companyService: ICompanyService;
  @IProjectService projectService: IProjectService;

  @observable private negotiationSubscription: any;
  @observable private proposalsSubscription: any;

  @observable public selectedVPPAIDs: string[] = [];
  @observable public selectedRFQIDs: string[] = [];
  @observable public selectedNegotiationID = '';
  @observable public warningMessage = '';
  @observable public allNegotiations: INegotiation[] = [];
  @observable public allStagedNegotiations: INegotiation[] = [];
  @observable public combinedNegotiations: INegotiation[] = [];
  @observable public allProposals: IProposal[] = [];
  @observable public negotiationInView: INegotiation;
  @observable public negotiationInViewDetails: NegotiationDetails = {} as NegotiationDetails;
  @observable public uniqueProjects: string[] = [];
  @observable public disabledCounter: boolean = true;
  @observable public disabledAcceptReject: boolean = true;
  @observable public validStatusMap: Map<VALID_STRUCTURED_CHAT_STATUS, Array<string>> = new Map();

  private negotiationStatusEnum: Array<EnumTranslate>;
  private contractTypes: Array<EnumTranslate>;

  private currentCompany;
  @observable public goToTradeConfirmationFlag: boolean = false;

  constructor() {
    this.enumService.getEnumValues(CASCADE_ENUM.NEGOTIATION_STATUS).then((response) => {
      if (response.REPLY) {
        this.negotiationStatusEnum = response.REPLY;
        this.negotiationStatusEnum.unshift({
          ENUM_VALUE: VALID_STRUCTURED_CHAT_STATUS.STAGED,
          READABLE_VALUE: 'Staged',
        });
      }
    });
    this.enumService.getFilteredEnumValues(CASCADE_ENUM.CONTRACT_TYPE).then((response) => {
      if (response.REPLY) {
        this.contractTypes = response.REPLY;
      }
    });
    this.validStatusMap.set(VALID_STRUCTURED_CHAT_STATUS.IN_PROGRESS, [
      VALID_STRUCTURED_CHAT_STATUS.IN_PROGRESS,
    ]);
    this.validStatusMap.set(VALID_STRUCTURED_CHAT_STATUS.REJECTED, [
      VALID_STRUCTURED_CHAT_STATUS.REJECTED,
    ]);
    this.validStatusMap.set(VALID_STRUCTURED_CHAT_STATUS.SUCCESSFUL, [
      VALID_STRUCTURED_CHAT_STATUS.SUCCESSFUL,
      USED_STRUCTURED_CHAT_STATUS.IN_PROGRESS_ARTERIA,
      USED_STRUCTURED_CHAT_STATUS.REVIEW_EXECUTION_CHECKLIST,
      USED_STRUCTURED_CHAT_STATUS.READY_FOR_TRADE_EXECUTION,
      USED_STRUCTURED_CHAT_STATUS.PENDING_EXECUTION_TIMER,
      USED_STRUCTURED_CHAT_STATUS.PENDING_EXECUTION,
      USED_STRUCTURED_CHAT_STATUS.EXECUTED_AWAITING_BUYER,
      USED_STRUCTURED_CHAT_STATUS.EXECUTED_AWAITING_SELLER,
      USED_STRUCTURED_CHAT_STATUS.EXPIRED,
      USED_STRUCTURED_CHAT_STATUS.BUYER_DELETED,
      USED_STRUCTURED_CHAT_STATUS.SELLER_DELETED,
    ]);
    this.validStatusMap.set(VALID_STRUCTURED_CHAT_STATUS.REQUIRES_CONFIRMATION, [
      VALID_STRUCTURED_CHAT_STATUS.REQUIRES_CONFIRMATION,
      USED_STRUCTURED_CHAT_STATUS.CONFIRMED_AWAITING_BUYER,
      USED_STRUCTURED_CHAT_STATUS.CONFIRMED_AWAITING_SELLER,
    ]);
    this.validStatusMap.set(VALID_STRUCTURED_CHAT_STATUS.STAGED, [
      VALID_STRUCTURED_CHAT_STATUS.STAGED,
    ]);
    this.validStatusMap.set(VALID_STRUCTURED_CHAT_STATUS.TRADED_AWAY, [
      VALID_STRUCTURED_CHAT_STATUS.TRADED_AWAY,
    ]);
  }

  public isCompanyAllowedToConfirmNegotiation(): boolean {
    if (
      this.negotiationInView.NEGOTIATION_STATUS ==
      VALID_STRUCTURED_CHAT_STATUS.REQUIRES_CONFIRMATION
    ) {
      return true;
    }

    if (
      (this.negotiationInView.NEGOTIATION_STATUS ==
        USED_STRUCTURED_CHAT_STATUS.CONFIRMED_AWAITING_BUYER &&
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID ===
          this.negotiationInView.BUYER_COMPANY_ID) ||
      (this.negotiationInView.NEGOTIATION_STATUS ==
        USED_STRUCTURED_CHAT_STATUS.CONFIRMED_AWAITING_SELLER &&
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID ===
          this.negotiationInView.SELLER_COMPANY_ID)
    ) {
      return true;
    }

    return false;
  }

  public isCompanyAllowedToReturnToNegotiation(): boolean {
    if (
      this.negotiationInView.NEGOTIATION_STATUS ==
        VALID_STRUCTURED_CHAT_STATUS.REQUIRES_CONFIRMATION ||
      this.negotiationInView.NEGOTIATION_STATUS ==
        USED_STRUCTURED_CHAT_STATUS.CONFIRMED_AWAITING_BUYER ||
      this.negotiationInView.NEGOTIATION_STATUS ==
        USED_STRUCTURED_CHAT_STATUS.CONFIRMED_AWAITING_SELLER
    ) {
      return true;
    }

    return false;
  }

  public openStructuredChat(selectedEntityIDs: ISCSelectedEntities) {
    this.currentCompany = this.companyService.getCurrentCompany();

    if (selectedEntityIDs) {
      this.selectedVPPAIDs = selectedEntityIDs.selectedVPPAIDs;
      this.selectedRFQIDs = selectedEntityIDs.selectedRFQIDs;
      this.selectedNegotiationID = selectedEntityIDs.selectedNegotiationID;
    }

    this.getNegotiations();
  }

  public closeStructuredChat() {
    this.selectedNegotiationID = '';
    this.selectedRFQIDs = [];
    this.selectedVPPAIDs = [];
    this.allProposals = [];
    this.allNegotiations = [];
    this.allStagedNegotiations = [];
    this.combinedNegotiations = [];
    this.negotiationInView = null;
    this.negotiationSubscription?.unsubscribe();
    this.proposalsSubscription?.unsubscribe();
    this.structuredChatVisibility = false;
    this.goToTradeConfirmationFlag = false;
  }

  public negotiationInViewChanged() {
    if (this.negotiationInView) {
      this.allProposals = [];
      this.selectedNegotiationID = this.negotiationInView.NEGOTIATION_ID;
      this.disabledAcceptReject = true;
      this.disabledCounter = true;
      this.collectDetails();

      if (this.negotiationInView.NEGOTIATION_STATUS !== VALID_STRUCTURED_CHAT_STATUS.STAGED) {
        this.getProposals();
      }
    } else {
      this.negotiationInViewDetails = {} as NegotiationDetails;
      this.negotiationInViewDetails.VPPA_UNIQUE_ID = null;
    }
  }

  public async selectedRFQIDsChanged() {
    if (this.selectedRFQIDs.length) {
      await this.generateStagedNegotiations();
      this.updateViews();
    }
  }

  public updateViews() {
    this.negotiationInView = this.combinedNegotiations.filter(
      (neg) => neg.NEGOTIATION_ID === this.selectedNegotiationID,
    )[0];
    this.disabledCounter = true;
  }

  // Get Negotiations data for the Left side list
  private async getNegotiations() {
    if (this.selectedRFQIDs.length && this.selectedVPPAIDs.length) {
      await this.generateStagedNegotiations();
    }

    let resultList = [];
    this.negotiationSubscription?.unsubscribe();

    let companyCriteria = '';
    if (
      this.userService.hasBuyerAndSellerVisibility() &&
      this.userService.getCurrentSide() === ProfileVisibility.SELLER
    ) {
      companyCriteria = `SELLER_COMPANY_ID == '${
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }' && `;
    } else if (
      this.userService.hasBuyerAndSellerVisibility() &&
      this.userService.getCurrentSide() === ProfileVisibility.BUYER
    ) {
      companyCriteria = `BUYER_COMPANY_ID == '${
        this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
      }' && `;
    }

    this.negotiationSubscription = this.negotiationService
      .getAllNegotiations({
        CRITERIA_MATCH: `${companyCriteria} (NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.IN_PROGRESS}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.REQUIRES_CONFIRMATION}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.CONFIRMED_AWAITING_BUYER}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.CONFIRMED_AWAITING_SELLER}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.SUCCESSFUL}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.REJECTED}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.IN_PROGRESS_ARTERIA}'|| NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.REVIEW_EXECUTION_CHECKLIST}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.READY_FOR_TRADE_EXECUTION}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.PENDING_EXECUTION_TIMER}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.PENDING_EXECUTION_TIMER}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.PENDING_EXECUTION}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.EXECUTED_AWAITING_BUYER}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.EXECUTED_AWAITING_SELLER}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.EXPIRED}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.BUYER_DELETED}' || NEGOTIATION_STATUS == '${USED_STRUCTURED_CHAT_STATUS.SELLER_DELETED}' || NEGOTIATION_STATUS == '${NEGOTIATION_STATUS.TRADED_AWAY}' )`,
      })
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            // I THINK THE API ONLY RETURNS CHANGED ROWS!!!!!! CHANGE TO TWO DIFFERENT VARIABLES FOR STAGES AND REGULAR!!!!!!!!
            this.allNegotiations = updateArray(resultList, this.allNegotiations);

            this.allNegotiations = this.allNegotiations.filter(
              (neg) =>
                neg.RFQ_CONTRACT_TYPE !== CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
                neg.VPPA_CONTRACT_TYPE !== CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED,
            );

            // If there is no existing negotiation selected and we are NOT trying to create a new one with VPPA/RFQ
            // then select the first existing negotiation
            if (
              !this.selectedNegotiationID &&
              !this.selectedVPPAIDs.length &&
              !this.selectedRFQIDs.length &&
              this.allNegotiations.length
            ) {
              this.selectedNegotiationID = this.allNegotiations[0].NEGOTIATION_ID;
            } else {
              this.checkSelectedNegotiationStatus();
            }
          } else {
            this.allNegotiations = [];
          }

          this.combinedNegotiations = [...this.allNegotiations, ...this.allStagedNegotiations];

          this.negotiationInView = this.combinedNegotiations.filter(
            (neg) => neg.NEGOTIATION_ID === this.selectedNegotiationID,
          )[0];

          this.uniqueProjects = [
            ...new Set(this.combinedNegotiations.map((item) => item.PROJECT_NAME)),
          ];

          this.structuredChatVisibility = true;
        },
        error: (error: any) => {
          this.allNegotiations = [];
          logger.error(error);
        },
      });
  }

  private checkSelectedNegotiationStatus() {
    if (!this.selectedNegotiationID) {
      return;
    }

    const selectedNegotiation = this.allNegotiations.filter(
      (negotiation) => negotiation.NEGOTIATION_ID == this.selectedNegotiationID,
    )[0];

    if (!selectedNegotiation) {
      return;
    }

    if (
      ['REQUIRES_CONFIRMATION', 'CONFIRMED_AWAITING_BUYER', 'CONFIRMED_AWAITING_SELLER'].includes(
        selectedNegotiation.NEGOTIATION_STATUS,
      )
    ) {
      this.goToTradeConfirmationFlag = true;
    }
  }

  // Get Proposal data for the chat bubbles
  private getProposals() {
    let resultList = [];
    this.proposalsSubscription?.unsubscribe();
    this.proposalsSubscription = this.negotiationService
      .getAllProposals(this.selectedNegotiationID)
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.allProposals = updateArray(resultList, this.allProposals).sort(
              (a, b) => b.CREATED_AT - a.CREATED_AT,
            );
            this.disabledAcceptReject = this.isLastCompanyProposal();
          } else {
            this.allProposals = [];
          }
        },
        error: (error: any) => {
          this.allProposals = [];
          logger.error(error);
        },
      });
  }

  private async generateStagedNegotiations() {
    const stagedNegotiations: INegotiation[] = [];
    let idCounter = 1;
    let validContractTypes = [];
    await this.enumService.getFilteredEnumValues(CASCADE_ENUM.PRODUCT_TYPE).then((response) => {
      if (response.REPLY) {
        validContractTypes = response.REPLY;
      }
    });

    for await (const vppaID of this.selectedVPPAIDs) {
      const vppa = await this.getVppaData(vppaID);

      for await (const rfqID of this.selectedRFQIDs) {
        const rfq = await this.getRFQData(rfqID);
        if (
          !validContractTypes.some((elm) => elm.ENUM_VALUE === rfq.PRODUCT_TYPE) ||
          !validContractTypes.some((elm) => elm.ENUM_VALUE === vppa.PRODUCT_TYPE)
        ) {
          return;
        }
        stagedNegotiations.push({
          PROJECT_ID: vppa.PROJECT_ID,
          VPPA_ID: vppa.VPPA_ID,
          VPPA_UNIQUE_ID: vppa.UNIQUE_ID,
          VPPA_TICKER: vppa.TICKER,
          RFQ_ID: rfq.RFQ_ID,
          RFQ_UNIQUE_ID: rfq.UNIQUE_ID,
          RFQ_TICKER: rfq.TICKER,
          VPPA_CONTRACT_TYPE: vppa.CONTRACT_TYPE,
          RFQ_CONTRACT_TYPE: rfq.CONTRACT_TYPE,
          BUYER_COMPANY_ID: rfq.CASCADE_COMPANY_ID,
          BUYER_COMPANY_NAME: rfq.COMPANY_NAME,
          SELLER_COMPANY_ID: vppa.CASCADE_COMPANY_ID,
          SELLER_COMPANY_NAME: vppa.COMPANY_NAME,
          CREATED_AT: null, // Dont need this value
          CREATED_BY: null, // Dont need this value
          MODIFIED_AT: null, // Dont need this value
          MODIFIED_BY: null, // Dont need this value
          NEGOTIATION_ID: idCounter.toString(),
          FIXED_PRICE: vppa.FIXED_PRICE,
          OFFERED_CAPACITY: vppa.OFFERED_CAPACITY,
          BUYER_POST_COD_COLLATERAL: rfq.BUYER_POST_COD_COLLATERAL,
          RFQ_CREDIT_PROFILE: rfq.RFQ_CREDIT_PROFILE,
          VPPA_CREDIT_PROFILE: vppa.VPPA_CREDIT_PROFILE,
          SELLER_POST_COD_COLLATERAL: vppa.SELLER_POST_COD_COLLATERAL,
          LAST_PROPOSAL_SENDER: '', // Need this value for masking
          TENOR_MONTHS: vppa.TENOR_MONTHS,
          TENOR_START_DATE: +vppa.TENOR_START_DATE,
          ISO: vppa.ISO, // Probably need to be the Project ISO but dont need it in the view
          PROJECT_NAME: vppa.PROJECT_NAME,
          TECHNOLOGY: vppa.TECHNOLOGY, // TODO: Probably needs to be the Project Technology rather than RFQ
          NEGOTIATION_STATUS: VALID_STRUCTURED_CHAT_STATUS.STAGED,
          BUYER_VISIBILITY: null, // Dont need this value
          SELLER_VISIBILITY: null, // Dont need this value
          RFQ_TECHNOLOGY: rfq.TECHNOLOGY,
          VPPA_TECHNOLOGY: vppa.TECHNOLOGY,
          RFQ_TARGET_COD: rfq.TARGET_COD,
          VPPA_TARGET_COD: vppa.TARGET_COD,
          VPPA_COD: vppa.COD,
          RFQ_GUARANTEED_COD: rfq.GUARANTEED_COD,
          VPPA_GUARANTEED_COD: vppa.GUARANTEED_COD,
          MEGAWATT_AC: vppa.MEGAWATT_AC,
          BUYERS_FRACTION: vppa.BUYERS_FRACTION,
          RFQ_TENOR_START_DATE: rfq.TENOR_START_DATE,
          VPPA_TENOR_START_DATE: vppa.TENOR_START_DATE,
          RFQ_TENOR_MONTHS: rfq.TENOR_MONTHS,
          VPPA_TENOR_MONTHS: vppa.TENOR_MONTHS,
          RFQ_FIXED_PRICE: rfq.FIXED_PRICE,
          VPPA_FIXED_PRICE: vppa.FIXED_PRICE,
          RFQ_ISO: rfq.ISO,
          VPPA_ISO: vppa.ISO,
          RFQ_SETTLEMENT_POINT: rfq.CONTRACT_TYPE === 'REC' ? 'REC' : rfq.SETTLEMENT_POINT,
          VPPA_SETTLEMENT_POINT: vppa.CONTRACT_TYPE === 'REC' ? 'REC' : vppa.SETTLEMENT_POINT,
          RFQ_SETTLEMENT_INDEX: rfq.CONTRACT_TYPE === 'REC' ? 'REC' : rfq.SETTLEMENT_INDEX_STRING,
          VPPA_SETTLEMENT_INDEX: vppa.CONTRACT_TYPE === 'REC' ? 'REC' : vppa.SETTLEMENT_INDEX,
          INTERCONNECTION_POINT: vppa.CONTRACT_TYPE === 'REC' ? 'REC' : vppa.INTERCONNECTION_POINT,
          RFQ_NEGATIVE_PRICING_METHOD:
            rfq.CONTRACT_TYPE === 'REC' ? 'REC' : rfq.RFQ_NEGATIVE_PRICING_METHOD,
          VPPA_NEGATIVE_PRICING_METHOD:
            vppa.CONTRACT_TYPE === 'REC' ? 'REC' : vppa.NEGATIVE_PRICING_METHOD,
          PROJECT_OPERATIONAL_STATUS: vppa.PROJECT_OPERATIONAL_STATUS,
          RFQ_APPLICABLE_REC_PROGRAM: rfq.RFQ_APPLICABLE_REC_PROGRAM,
          VPPA_APPLICABLE_REC_PROGRAM: vppa.VPPA_APPLICABLE_REC_PROGRAM,
          RFQ_REC_DELIVERY_METHODOLOGY: rfq.RFQ_REC_DELIVERY_METHODOLOGY,
          VPPA_REC_DELIVERY_METHODOLOGY: vppa.VPPA_REC_DELIVERY_METHODOLOGY,
          RFQ_APPLICABLE_TRACKING_SYSTEM: rfq.APPLICABLE_TRACKING_SYSTEM,
          VPPA_APPLICABLE_TRACKING_SYSTEM: vppa.VPPA_APPLICABLE_TRACKING_SYSTEM,
          RFQ_PERFORMANCE_GUARANTEE_METHOD: rfq.RFQ_PERFORMANCE_GUARANTEE_METHOD,
          VPPA_PERFORMANCE_GUARANTEE_METHOD: vppa.PERFORMANCE_GUARANTEE_METHOD,
          NAME: vppa.PROJECT_NAME,
        });

        if (idCounter === 1) {
          this.selectedNegotiationID = idCounter.toString();
        }

        idCounter += 1;
      }
    }

    this.allStagedNegotiations = stagedNegotiations;
    this.combinedNegotiations = this.combinedNegotiations.filter(
      (neg) => neg.NEGOTIATION_STATUS !== VALID_STRUCTURED_CHAT_STATUS.STAGED,
    );
    this.combinedNegotiations.push(...this.allStagedNegotiations);
  }

  // If existing negotiation get VPPA and RFQ ID from negotiation
  private collectDetails() {
    let _vppa = null;
    let _rfq = null;

    this.getVppaData().then((vppa) => {
      if (vppa) {
        _vppa = vppa;

        this.getRFQData().then((rfq) => {
          if (rfq) {
            _rfq = rfq;
            this.updateNegotiationDetails(_vppa, _rfq);
          }
        });
      }
    });
  }

  // Add Project, VPPA and RFQ values to an object that is used to
  // display them in the top right section of the Structured Chat
  private async updateNegotiationDetails(vppa, rfq) {
    let tempNeg = {} as NegotiationDetails;

    let rfqOfferedCapacity = '';
    let vppaVolume;
    await this.vppaService.getProjectKeyMetrics(vppa.VPPA_ID).then((response) => {
      const hoursOnYear = 8760;
      if (response && response.REPLY.length > 0 && response.REPLY[0].IMPLIED_CAPACITY_FACTOR) {
        const impliedCapacityFactor = Number(response.REPLY[0].IMPLIED_CAPACITY_FACTOR.toFixed(2));
        rfqOfferedCapacity = (
          rfq.REQUESTED_VOLUME /
          (hoursOnYear * impliedCapacityFactor)
        ).toString();

        vppaVolume = vppa.OFFERED_CAPACITY * hoursOnYear * impliedCapacityFactor;
      }
    });

    const rfqFixedPrice =
      rfq.PRODUCT_TYPE === PRODUCT_TYPE.RECS
        ? rfq.FIXED_PRICE
        : rfq.FIXED_PRICE_LOWER_BOUND + ' - ' + rfq.FIXED_PRICE_UPPER_BOUND;

    tempNeg = {
      PROJECT_ID: vppa.PROJECT_ID,
      PROJECT_NAME: vppa.PROJECT_NAME,
      VPPA_TICKER: vppa.TICKER,
      VPPA_UNIQUE_ID: vppa.UNIQUE_ID,
      VPPA_FIXED_PRICE: vppa.FIXED_PRICE,
      VPPA_OFFERED_CAPACITY: vppa.OFFERED_CAPACITY,
      VPPA_VOLUME: vppaVolume,
      VPPA_TENOR_START_DATE: vppa.TENOR_START_DATE,
      VPPA_TENOR_MONTHS: vppa.TENOR_MONTHS,
      VPPA_CREDIT_PROFILE: vppa.VPPA_CREDIT_PROFILE,
      VPPA_COLLATERAL: vppa.SELLER_POST_COD_COLLATERAL,
      RFQ_TICKER: rfq.TICKER,
      RFQ_UNIQUE_ID: rfq.UNIQUE_ID,
      RFQ_FIXED_PRICE: rfqFixedPrice,
      RFQ_OFFERED_CAPACITY: rfqOfferedCapacity,
      RFQ_VOLUME: rfq.REQUESTED_VOLUME,
      RFQ_TENOR_START_DATE: rfq.TENOR_START_DATE,
      RFQ_TENOR_MONTHS: rfq.TENOR_MONTHS,
      RFQ_CREDIT_PROFILE: rfq.RFQ_CREDIT_PROFILE,
      RFQ_COLLATERAL: rfq.BUYER_POST_COD_COLLATERAL,
    } as NegotiationDetails;

    const negStatus = getEnumReadableValue(
      this.negotiationStatusEnum,
      this.negotiationInView.NEGOTIATION_STATUS,
    );

    tempNeg.NEGOTIATION_STATUS_ENUM_VALUE = this.negotiationInView.NEGOTIATION_STATUS;
    tempNeg.NEGOTIATION_STATUS = negStatus;
    tempNeg.NEGOTIATION_STATUS_CLASS = negStatus.replace(/\s/g, '').toLowerCase();
    tempNeg.COMPANY_NAME =
      this.currentCompany.CASCADE_COMPANY_ID == this.negotiationInView.BUYER_COMPANY_ID
        ? this.negotiationInView.SELLER_COMPANY_NAME
        : this.negotiationInView.BUYER_COMPANY_NAME;

    tempNeg.COMPANY_NAME =
      this.currentCompany.CASCADE_COMPANY_ID == vppa.CASCADE_COMPANY_ID
        ? rfq.COMPANY_NAME
        : vppa.COMPANY_NAME;

    this.negotiationInViewDetails = tempNeg;

    if (rfq.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
      this.warningMessage =
        'This contract description is intended to summarize the principal terms of a proposal regarding a potential REC purchase contract between the parties. It is only an expression of the parties’ intent, is not legally binding, and is subject to the execution of a definitive agreement';
    } else {
      this.warningMessage = `This contract description is intended to summarize the principal terms of a proposal regarding a potential ${getEnumReadableValue(this.contractTypes, vppa.CONTRACT_TYPE)} between the parties. It is only an expression of the parties’ intent, is not legally binding, and is subject to the execution of a definitive agreement and the completion of all other steps required by the SEF.`;
    }
  }

  private getVppaData(vppaID?: string) {
    return this.vppaService
      .getVppa(vppaID ? vppaID : this.negotiationInView.VPPA_ID)
      .then((response) => {
        if (response) {
          return response.REPLY[0] as IVppa;
        }

        return null;
      });
  }

  private getRFQData(rfqID?: string) {
    return this.rfqService.getRfq(rfqID ? rfqID : this.negotiationInView.RFQ_ID).then(
      (response) => {
        if (response) {
          return response.REPLY[0] as IRfq;
        }

        return null;
      }
    );
  }

  public sellerMaskingWarning(): string {
    if (!this.allProposals.length) {
      return 'By selecting the "Send Refined Terms" option below, the Seller consents to revealing their Company and Project Identity, to the Buyer Company that is party to this negotiation.';
    } else if (this.allProposals.length == 1) {
      return 'By selecting the Counter or Accept option below, the Seller consents to revealing their Company and Project Identity, to the Buyer Company that is party to this negotiation';
    }
  }

  // Determines whether a proposal belongs to the currently logged in User's Company
  public isCompanyProposal(proposal?: IProposal): boolean {
    if (!proposal && !this.allProposals.length) {
      return false;
    }

    const _proposal = proposal ? proposal : this.allProposals[0];

    if (
      (this.currentCompany.CASCADE_COMPANY_ID === _proposal.BUYER_COMPANY_ID &&
        _proposal.PROPOSAL_SENDER === 'BUYER') ||
      (this.currentCompany.CASCADE_COMPANY_ID === _proposal.SELLER_COMPANY_ID &&
        _proposal.PROPOSAL_SENDER === 'SELLER')
    ) {
      return true;
    } else {
      return false;
    }
  }

  public isLastCompanyProposal(): boolean {
    if (!this.allProposals.length) {
      return false;
    } else if (
      (this.currentCompany.CASCADE_COMPANY_ID === this.negotiationInView.BUYER_COMPANY_ID &&
        this.negotiationInView.LAST_PROPOSAL_SENDER === 'BUYER') ||
      (this.currentCompany.CASCADE_COMPANY_ID === this.negotiationInView.SELLER_COMPANY_ID &&
        this.negotiationInView.LAST_PROPOSAL_SENDER === 'SELLER')
    ) {
      return true;
    } else {
      return false;
    }
  }

  public getLastProposalSenderName(): string {
    if (!this.negotiationInView) {
      return '';
    }

    if (this.negotiationInView.LAST_PROPOSAL_SENDER == 'BUYER') {
      return this.negotiationInView.BUYER_COMPANY_NAME;
    } else {
      return this.negotiationInView.SELLER_COMPANY_NAME;
    }
  }

  public removeStagedNegotiation(negotiationID: string, newNegotiationID: string) {
    if (this.negotiationInView.NEGOTIATION_STATUS === VALID_STRUCTURED_CHAT_STATUS.STAGED) {
      this.allStagedNegotiations = this.allStagedNegotiations.filter(
        (neg) => neg.NEGOTIATION_ID !== negotiationID,
      );
      this.selectedNegotiationID = this.allStagedNegotiations.length
        ? this.allStagedNegotiations[0].NEGOTIATION_ID
        : newNegotiationID;
      this.updateViews();
    }
  }
}

export const IStructuredChatService = DI.createInterface<IStructuredChatService>((x) =>
  x.singleton(StructuredChatServiceImpl),
);
